import React, { useMemo } from 'react'
import { OrderSummary } from '../../../../components/OrderSummary'
import {
  calculateTotalDiscount,
  calculateTotalGST,
  calculateDiscountedPriceIncGst,
} from '../../utils/calculateTotal'
import { addGST } from '../../../../util/addGst'
import { CartItem } from '../../../cart/storage/cart.types'
import { formatPrice } from '../../../../util/helpers'
import { Box, Typography } from '@mui/material'
import TestIds from '../../../../testIds'
import { isEqual, pick } from 'lodash'
import { CartMessage } from '../../../cart/utils/cartMessages'
import { colors } from '../../../../themes/colors'

interface QuoteSummaryTotalProps {
  products: CartItem[]
  originalProducts?: CartItem[]
  accountName?: string
  accountNumber?: string
  freightChargeExcGst: number | null
  showDivider?: boolean
  title?: string
  backgroundColor?: string
}

export const QuoteSummaryTotal = ({
  products,
  originalProducts,
  accountName,
  accountNumber,
  freightChargeExcGst,
  showDivider = true,
  title = 'Quote Summary',
  backgroundColor,
}: QuoteSummaryTotalProps) => {
  const total = useMemo(
    () => formatPrice(calculateDiscountedPriceIncGst(products, freightChargeExcGst)),
    [products],
  )

  //if there are changes in the product quantities between the current and original products, show message.
  const freightMessage = useMemo(() => {
    if (!originalProducts || !freightChargeExcGst) return null
    const productQuantities = products.map((p) => pick(p, ['quantity', 'productId']))
    const originalProductQuantities = originalProducts.map((p) =>
      pick(p, ['quantity', 'productId']),
    )
    if (isEqual(productQuantities, originalProductQuantities)) return null

    return (
      <Box
        component='span'
        display='flex'
        flex={'1 0 auto'}
        borderLeft={`5px solid ${colors.primary500}`}
        paddingLeft={'16px'}
        sx={{ overflowWrap: 'anywhere', flexDirection: 'column' }}
      >
        <Typography
          variant='body2'
          fontWeight={600}
          data-testid={TestIds.feat.cart.summary.deliveryCostNotice}
        >
          {CartMessage.deliceryCostAdjustableTitle}
          <br />
          <Typography variant='body2' component='span'>
            {CartMessage.deliveryCostAdjustable}
          </Typography>
        </Typography>
      </Box>
    )
  }, [products, originalProducts, freightChargeExcGst])

  const gst = useMemo(
    () => formatPrice(calculateTotalGST(products, freightChargeExcGst)),
    [products],
  )

  const deliveryCost = useMemo(() => {
    if (freightChargeExcGst) {
      return formatPrice(addGST(freightChargeExcGst))
    }
    return undefined
  }, [freightChargeExcGst])

  const discount = useMemo(() => {
    const discountTotal = calculateTotalDiscount(products)
    if (discountTotal > 0) {
      return formatPrice(discountTotal)
    }
    return undefined
  }, [products])

  return (
    <>
      <OrderSummary
        title={title}
        accountName={accountName}
        accountNumber={accountNumber}
        total={total}
        gst={gst}
        delivery={deliveryCost}
        discount={discount}
        showDivider={showDivider}
        backgroundColor={backgroundColor}
      />
      {freightMessage}
    </>
  )
}
